import { apiClient } from '../api-client'
import { Project, CreateProjectRequest, Task, Milestone, User, TaskFilters } from '@/types'
import { StoredAttachment } from './file-storage-api'

export interface ProjectSecretsFixedPayload {
  subscription_key?: string
  admin_username?: string
  admin_password?: string
  deployment_type?: string
  production_url?: string
}

export interface ProjectSecretFieldInput {
  label: string
  value: string
  is_encrypted: boolean
}

export interface ProjectSecretFieldItem extends ProjectSecretFieldInput {
  id: string
  secret_key: string
}

export interface ProjectSecretsResponse extends ProjectSecretsFixedPayload {
  custom_fields: ProjectSecretFieldItem[]
}

export interface ProjectTeamMemberWorkSummary {
  user_id: string
  tasks_assigned: number
  tasks_completed: number
  bugs_assigned: number
  bugs_fixed: number
}

export interface PaginatedProjectsResponse {
  data: Project[]
  total: number
  page: number
  limit: number
  total_pages: number
}

const PROJECT_PAGE_SIZE = 100
const PROJECT_MAX_PAGES = 50

function extractProjectRows(response: any): any[] {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.projects)) return response.projects
  if (Array.isArray(response?.items)) return response.items
  if (Array.isArray(response?.results)) return response.results
  return []
}

function normalizeProjectRow(project: any): Project {
  const resolvedId = String(project?.id ?? project?.project_id ?? project?.projectId ?? '').trim()
  const resolvedName = String(
    project?.name ??
    project?.project_name ??
    project?.projectName ??
    project?.title ??
    project?.display_name ??
    'Unknown Project'
  ).trim()
  const resolvedKey = String(project?.key ?? project?.project_key ?? project?.projectKey ?? '').trim()

  return {
    ...project,
    id: resolvedId || String(project?.id || ''),
    name: resolvedName || 'Unknown Project',
    key: resolvedKey || project?.key || '',
  } as Project
}

// Helper function to convert camelCase to snake_case
function toSnakeCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(toSnakeCase)
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      acc[snakeKey] = toSnakeCase(obj[key])
      return acc
    }, {} as any)
  }
  return obj
}

// Projects API
export const projectsApi = {
  getProjects: async (filters?: any): Promise<Project[]> => {
    const baseParams = { ...(filters || {}) }
    const hasExplicitPaging = Object.prototype.hasOwnProperty.call(baseParams, 'skip') || Object.prototype.hasOwnProperty.call(baseParams, 'limit')

    if (hasExplicitPaging) {
      const response = await apiClient.get<any>('/projects', { params: baseParams })
      return extractProjectRows(response).map(normalizeProjectRow)
    }

    try {
      let skip = 0
      const allRows: Project[] = []
      let hasMore = true
      let safetyPageCount = 0

      while (hasMore && safetyPageCount < PROJECT_MAX_PAGES) {
        const params = { ...baseParams, skip, limit: PROJECT_PAGE_SIZE }
        const response = await apiClient.get<any>('/projects', { params })
        const rows = extractProjectRows(response).map(normalizeProjectRow)

        if (!rows.length) {
          break
        }

        allRows.push(...rows)
        if (rows.length < PROJECT_PAGE_SIZE) {
          hasMore = false
        } else {
          skip += PROJECT_PAGE_SIZE
        }
        safetyPageCount += 1
      }

      return allRows
    } catch {
      // Fallback for environments where non-paginated/project-list behavior differs.
      const fallback = await apiClient.get<any>('/projects', {
        params: { ...baseParams, skip: 0, limit: PROJECT_PAGE_SIZE, paginated: true },
      })
      return extractProjectRows(fallback).map(normalizeProjectRow)
    }
  },

  getProjectsPaginated: (
    filters?: any,
    page = 1,
    limit = 20
  ): Promise<PaginatedProjectsResponse> => {
    const safeLimit = Math.min(Math.max(limit, 1), 100)
    const safePage = Math.max(page, 1)
    const skip = (safePage - 1) * safeLimit
    return apiClient.get('/projects', {
      params: { ...(filters || {}), skip, limit: safeLimit, paginated: true },
    })
  },

  getProject: (id: string): Promise<Project> =>
    apiClient.get(`/projects/${id}`),

  createProject: (data: CreateProjectRequest): Promise<Project> => {
    // Convert camelCase to snake_case for backend compatibility
    const snakeData = toSnakeCase(data)

    // Let backend auto-generate key when the field is empty.
    if (!snakeData.key || String(snakeData.key).trim() === '') {
      delete snakeData.key
    }

    console.log('API: createProject sending snake_case data:', snakeData)
    return apiClient.post('/projects', snakeData)
  },

  updateProject: (id: string, data: Partial<CreateProjectRequest>): Promise<Project> => {
    const snakeData = toSnakeCase(data)
    if (!snakeData.key || String(snakeData.key).trim() === '') {
      delete snakeData.key
    }
    return apiClient.put(`/projects/${id}`, snakeData)
  },

  deleteProject: (id: string): Promise<void> =>
    apiClient.delete(`/projects/${id}`),

  archiveProject: (id: string): Promise<Project> =>
    apiClient.post(`/projects/${id}/archive`, {}),

  restoreProject: (id: string): Promise<Project> =>
    apiClient.post(`/projects/${id}/restore`, {}),

  duplicateProject: (
    id: string,
    payload: {
      new_name: string
      new_key?: string
      include_team_members?: boolean
      adjust_dates?: boolean
      date_offset_days?: number | null
      reset_budget?: boolean
    }
  ): Promise<Project> =>
    apiClient.post(`/projects/${id}/duplicate`, payload),

  getProjectTasks: async (id: string, filters?: TaskFilters): Promise<Task[]> => {
    const pageSize = 200
    let skip = 0
    const all: Task[] = []
    let hasMore = true

    while (hasMore) {
      const batch: Task[] = await apiClient.get(`/projects/${id}/tasks`, {
        params: { ...filters, skip, limit: pageSize }
      })
      const rows = Array.isArray(batch) ? batch : []
      all.push(...rows)

      if (rows.length < pageSize) {
        hasMore = false
      } else {
        skip += pageSize
      }
    }

    return all
  },

  getProjectMilestones: (id: string): Promise<Milestone[]> =>
    apiClient.get(`/projects/${id}/milestones`),

  getProjectTeam: (id: string): Promise<User[]> =>
    apiClient.get(`/projects/${id}/team`),

  getProjectTeamWorkSummary: (id: string): Promise<ProjectTeamMemberWorkSummary[]> =>
    apiClient.get(`/projects/${id}/team/work-summary`),

  getProjectActivity: (id: string, skip = 0, limit = 50): Promise<{ project_id: string; activities: any[]; total: number }> =>
    apiClient.get(`/projects/${id}/activity`, { params: { skip, limit } }),

  addProjectMember: (projectId: string, memberData: { user_id: string; role: string }): Promise<void> =>
    apiClient.post(`/projects/${projectId}/members`, memberData),

  removeProjectMember: (projectId: string, userId: string): Promise<void> =>
    apiClient.delete(`/projects/${projectId}/members/${userId}`),

  // Project Documents
  listProjectDocuments: (projectId: string): Promise<StoredAttachment[]> =>
    apiClient.get(`/projects/${projectId}/documents`),

  updateProjectDocument: (projectId: string, attachmentId: string, payload: {
    title?: string
    key?: string
    filename?: string
    original_filename?: string
    file_size?: number
    mime_type?: string
  }): Promise<StoredAttachment> =>
    apiClient.patch(`/projects/${projectId}/documents/${attachmentId}`, payload),

  deleteProjectDocument: (projectId: string, attachmentId: string): Promise<{ message: string }> =>
    apiClient.delete(`/projects/${projectId}/documents/${attachmentId}`),

  // Project Secrets
  getProjectSecrets: (projectId: string): Promise<ProjectSecretsResponse> =>
    apiClient.get(`/projects/${projectId}/secrets`),

  updateProjectFixedSecrets: (projectId: string, payload: ProjectSecretsFixedPayload): Promise<ProjectSecretsResponse> =>
    apiClient.put(`/projects/${projectId}/secrets/fixed`, payload),

  createProjectCustomSecret: (projectId: string, payload: ProjectSecretFieldInput): Promise<ProjectSecretFieldItem> =>
    apiClient.post(`/projects/${projectId}/secrets/custom`, payload),

  updateProjectCustomSecret: (projectId: string, entryId: string, payload: ProjectSecretFieldInput): Promise<ProjectSecretFieldItem> =>
    apiClient.patch(`/projects/${projectId}/secrets/custom/${entryId}`, payload),

  deleteProjectCustomSecret: (projectId: string, entryId: string): Promise<{ message: string }> =>
    apiClient.delete(`/projects/${projectId}/secrets/custom/${entryId}`),

}
